/**
 * Per-test data-settings page — "How this test uses its data"
 * (#1258 Slice 3 → reduced to the reuse choice in #1374 Slice 4).
 *
 * Route: /workspaces/:wid/test-data/:setId/bindings/:scriptId
 *
 * Slice 4 strips this page down to the ONE choice an operator makes
 * about how a test uses its data: reuse rows freely, or use each row
 * once. Everything else the old page carried is gone:
 *
 *   - The field→column mapper UI is DEFERRED — re-matching a field to a
 *     different column would force the linked script to be regenerated,
 *     so it isn't built now; columns auto-match to fields by name.
 *   - The rows-per-run / order / start-row controls are REMOVED — they
 *     were the dead per-run fan-out config the unified model never ran.
 *     "Run this test against every row" is a per-run choice on the run
 *     template ("Test data → All rows"), not a per-test setting.
 *
 * Matches `docs/mockups/test-data-settings.html`. The reuse choice
 * saves on selection (no Save button — the mockup is buttonless). When
 * the test's Set was removed, this page surfaces the recovery path
 * ("pick a new one") rather than a dead binding.
 *
 * Plain language only: "use each row once" / "reuse rows freely" /
 * "Used". No "binding" / "consumption" / "iteration" / "pool" /
 * "$CONSUMED" in any operator-facing string.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { ChevronLeft, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  testDataSetsAPI,
  dataBindingsAPI,
  type TestDataSet,
  type DataBindingDto,
  type DataBindingConsumption,
  type ScriptFieldDescriptor,
} from '@/lib/api'
import { useToast } from '@/hooks/useToast'
import { useConfirm } from '@/hooks/useConfirm'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'

/** Auto-match a script field name against the Set's column names so a
 * first-time create can wire fields to columns without the (deferred)
 * mapper UI. Exact case-insensitive match wins; falls back to an
 * alphanumeric-normalized match (e.g. `email_address` ↔ `Email Address`). */
function autoMatch(fieldName: string, columnNames: string[]): string | undefined {
  const lc = fieldName.toLowerCase()
  for (const c of columnNames) if (c.toLowerCase() === lc) return c
  const normalized = lc.replace(/[^a-z0-9]+/g, '')
  for (const c of columnNames) {
    if (c.toLowerCase().replace(/[^a-z0-9]+/g, '') === normalized) return c
  }
  return undefined
}

export default function TestDataBindingDetail() {
  const { setId, scriptId } = useParams<{ setId: string; scriptId: string }>()
  const navigate = useNavigate()
  const wp = useWorkspacePath()
  const toast = useToast()
  const confirm = useConfirm()

  const [set, setSet] = useState<TestDataSet | null>(null)
  const [binding, setBinding] = useState<DataBindingDto | null>(null)
  const [scriptName, setScriptName] = useState('')
  const [scriptFields, setScriptFields] = useState<ScriptFieldDescriptor[]>([])
  const [consumption, setConsumption] = useState<DataBindingConsumption>('reusable')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!setId || !scriptId) return
      setLoading(true)
      setError(null)
      try {
        const [setResp, fieldsResp, bindingResp] = await Promise.all([
          testDataSetsAPI.get(setId),
          dataBindingsAPI.getScriptFields(scriptId),
          dataBindingsAPI.getByScript(scriptId),
        ])
        if (cancelled) return
        // A live binding that points at a DIFFERENT Set means this URL is
        // stale — surface it rather than silently editing the wrong test.
        // An orphaned binding (setId null) is allowed through: this page
        // is where the operator re-connects it.
        if (bindingResp && bindingResp.setId && bindingResp.setId !== setId) {
          setError(
            'This test reads from a different Test Data Set. Open it from that Set\'s "Tests using this Set" tab.',
          )
          return
        }
        setSet(setResp.set)
        setScriptFields(fieldsResp.fields)
        setScriptName(fieldsResp.scriptName)
        setBinding(bindingResp)
        if (bindingResp) setConsumption(bindingResp.consumption)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [setId, scriptId])

  // Save the reuse choice on selection (the mockup has no Save button).
  // First selection on an unbound test creates the connection (columns
  // auto-match to fields by name); thereafter it just updates the choice.
  async function chooseReuse(next: DataBindingConsumption) {
    if (!scriptId || !setId || !set || next === consumption || saving) return
    const previous = consumption
    setConsumption(next) // optimistic
    setSaving(true)
    try {
      if (binding) {
        const updated = await dataBindingsAPI.updateConsumption(binding._id, next)
        setBinding(updated)
      } else {
        const userColNames = set.columns.filter((c) => !c.system).map((c) => c.name)
        const fieldMappings: Record<string, string> = {}
        for (const f of scriptFields) {
          const m = autoMatch(f.name, userColNames)
          if (m) fieldMappings[f.name] = m
        }
        const created = await dataBindingsAPI.create({
          scriptId,
          setId,
          fieldMappings,
          consumption: next,
        })
        setBinding(created)
      }
      toast({ title: 'Saved', variant: 'success' })
    } catch (err) {
      setConsumption(previous) // roll back the optimistic flip
      toast({
        title: 'Could not save',
        description: err instanceof Error ? err.message : String(err),
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleStopUsing() {
    if (!binding) return
    const ok = await confirm({
      title: 'Stop using this Set for this test?',
      description:
        'This test will no longer read from a Test Data Set. The test and the Set are not deleted.',
      confirmLabel: 'Stop using',
      destructive: true,
    })
    if (!ok) return
    try {
      await dataBindingsAPI.delete(binding._id)
      toast({ title: 'Stopped using the Set', variant: 'success' })
      navigate(wp(`/test-data/${setId}`))
    } catch (err) {
      toast({
        title: 'Could not stop',
        description: err instanceof Error ? err.message : String(err),
        variant: 'error',
      })
    }
  }

  if (loading) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    )
  }
  if (error || !set) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(wp(`/test-data/${setId}`))} className="mb-4">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="border border-destructive bg-destructive/10 text-destructive rounded p-3">
          {error ?? 'Could not load this test\'s data settings.'}
        </div>
      </div>
    )
  }

  const orphaned = Boolean(binding?.wasOrphaned)

  return (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <nav className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
        <button className="hover:text-foreground" onClick={() => navigate(wp('/tests'))}>
          Tests
        </button>
        <span>›</span>
        <span className="truncate max-w-[280px]">{scriptName || 'This test'}</span>
        <span>›</span>
        <span className="text-foreground font-medium">Test data</span>
      </nav>

      <h1 className="text-xl font-semibold">How this test uses its data</h1>
      {orphaned ? (
        // Removed-Set recovery. When the Set was deleted this test isn't
        // reading any data, so we DON'T show "Reads from <set>", the
        // reuse choice, or "Stop using" — they'd be misleading. The page
        // is a clean pointer to pick a new Set (recovery happens in the
        // Test Data catalog, where the Set's add-a-test picker connects).
        <div className="mt-4 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-4 text-sm text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
          <div>
            The Test Data Set this test used was removed, so this test isn’t reading
            any data right now. Pick a new one to keep running it —{' '}
            <Link to={wp('/test-data')} className="font-medium underline hover:no-underline">
              choose a Set
            </Link>
            .
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mt-1.5">
            Reads from <span className="font-medium text-foreground">{set.name}</span>
            {' · '}
            <Link to={wp(`/test-data/${set._id}`)} className="text-primary hover:underline">
              Open the Set
            </Link>
            {binding && (
              <>
                {' · '}
                <button onClick={handleStopUsing} className="text-primary hover:underline">
                  Stop using this Set
                </button>
              </>
            )}
          </p>

          {/* The one choice: reuse rows, or use each once */}
          <div className="mt-5 rounded-xl border bg-card p-5 shadow-sm">
            <h2 className="text-sm font-semibold" id="reuse-choice-heading">
              Reuse rows, or use each once
            </h2>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              Choose what happens to a row after a run uses it.
            </p>
            <div
              role="radiogroup"
              aria-labelledby="reuse-choice-heading"
              className="flex flex-col sm:flex-row gap-3"
            >
              <ReuseChoice
                selected={consumption === 'single-use'}
                disabled={saving}
                title="Use each row once"
                description="After a run uses a row, mark it Used so the next run takes a fresh one. Good for sign-ups, promo codes, one-time data."
                onSelect={() => chooseReuse('single-use')}
              />
              <ReuseChoice
                selected={consumption === 'reusable'}
                disabled={saving}
                title="Reuse rows freely"
                description="Every run may use any row. Good for data that doesn’t get spent."
                onSelect={() => chooseReuse('reusable')}
              />
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-dashed bg-muted/30 px-3.5 py-2.5 text-xs text-muted-foreground">
            Want to run this test against <span className="font-medium text-foreground">every</span>{' '}
            row at once? That’s a run option — pick{' '}
            <span className="font-medium text-foreground">Test data → All rows</span> when you start a
            run. It runs the test once per row and doesn’t change any row’s status.
          </div>
        </>
      )}
    </div>
  )
}

/** One reuse-choice card (radio semantics). Plain title + description;
 * the whole card is the click target. */
function ReuseChoice({
  selected,
  disabled,
  title,
  description,
  onSelect,
}: {
  selected: boolean
  disabled: boolean
  title: string
  description: string
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`flex-1 text-left flex gap-2.5 rounded-lg border p-3.5 transition-colors disabled:opacity-60 ${
        selected ? 'border-primary bg-primary/5' : 'border-border hover:border-border-strong'
      }`}
    >
      <span
        className={`mt-0.5 h-3.5 w-3.5 flex-none rounded-full border-2 ${
          selected ? 'border-primary bg-primary ring-2 ring-inset ring-background' : 'border-muted-foreground/40'
        }`}
      />
      <span>
        <span className="block text-[13px] font-semibold">{title}</span>
        <span className="block text-xs text-muted-foreground mt-1">{description}</span>
      </span>
    </button>
  )
}
